/**
 * Pixel-first analytics bridge.
 *
 * Instantiated once as `WYATT.Analytics` when this module loads.
 * The `<wyatt-analytics>` custom element parses its payload and calls
 * `fromElement()` on connect (or on drawer/modal open when nested in a
 * closed dialog), except `select_item` which publishes on link click.
 * Events go through `Shopify.analytics.publish`. Theme script tags sit before
 * `content_for_header`, but this module is deferred so Shopify is usually
 * ready; `publish()` still queues until the API exists. `publish()` is a
 * no-op when `Shopify.designMode` or `Shopify.inspectMode` is true (theme
 * editor / inspector).
 */

/**
 * @returns {((name: string, data: object) => void)|null}
 */
function getShopifyPublish() {
  return window.Shopify?.analytics?.publish ?? null;
}

/**
 * @param {string} name
 * @param {object} data
 */
function log(name, data) {
  if (window.Shopify?.theme?.role === "development") {
    // eslint-disable-next-line no-console -- dev-theme pixel debugging
    console.log("[WYATT.Analytics] publish", { name, data });
  }
}

class Analytics {
  events = {
    page_context: "wyatt:page_context",
    view_mini_cart: "wyatt:view_mini_cart",
    view_item_list: "wyatt:view_item_list",
    select_item: "wyatt:select_item",
    select_store: "wyatt:select_store",
  };

  /** @type {[string, object][]} */
  #pendingPublishes = [];

  /**
   * Publish from a `<wyatt-analytics data-event="…">` element using its
   * already-parsed `payload` property. `data-event` matches `events` keys
   * (snake_case).
   *
   * @param {Element & { payload?: object }} el
   */
  fromElement(el) {
    const event = el?.dataset?.event;

    if (!event || el.payload == null) {
      return;
    }

    this.publish(this.events[event], el.payload);
  }

  /**
   * @param {string} name
   * @param {object} [data]
   */
  publish(name, data = {}) {
    if (window.Shopify?.designMode || window.Shopify?.inspectMode) {
      return;
    }

    try {
      this.#publishPending();

      const shopifyPublish = getShopifyPublish();

      if (!shopifyPublish) {
        this.#pendingPublishes.push([name, data]);
        return;
      }

      log(name, data);
      shopifyPublish(name, data);
    } catch {
      // noop
    }
  }

  #publishPending() {
    const shopifyPublish = getShopifyPublish();

    if (!shopifyPublish || this.#pendingPublishes.length === 0) {
      return;
    }

    while (this.#pendingPublishes.length > 0) {
      const [name, data] = this.#pendingPublishes.shift();

      try {
        log(name, data);
        shopifyPublish(name, data);
      } catch {
        // noop
      }
    }
  }
}

WYATT.Analytics = new Analytics();

export default Analytics;
